/**
 * ESCHER FALLS — 面ごとのタイム計測。METAGAME-SPEC §1。
 *
 * **壁時計（`Date.now`）ではなく、メインループの dt で進める。** 理由は3つある。
 *
 * 1. タブが非表示のとき `requestAnimationFrame` は止まるので、
 *    「放置した時間を計らない」（§1.2）が**何も書かずに満たされる**。
 *    `visibilitychange` を自前で捌くと、開発時のブラウザペインは常時 `hidden` なので
 *    タイマーが一切動かなくなる（VERIFY-NOTES §1 の罠）。
 * 2. `game.update(dt)` を手で回すヘッドレス検証で、そのまま時間を進められる。
 * 3. 純関数的に書けるのでテストできる。
 *
 * main.ts は dt を `MAX_DT_MS` で頭打ちにしているため、長時間の離席から戻っても
 * 1フレームぶんしか加算されない。これは意図した挙動である。
 */

/** 計測の状態。 */
export type TimerPhase = 'idle' | 'running' | 'paused' | 'stopped';

export interface LevelTimer {
  /** 経過時間 (ms)。`stop()` 後は確定値のまま動かない。 */
  readonly elapsedMs: number;
  readonly phase: TimerPhase;
  /** ゼロから計測を開始する。面を読み込んだあと、盤面を最初に操作した瞬間に呼ぶ。 */
  start(): void;
  /** 一時停止。経過時間は保持する。 */
  pause(): void;
  /** 一時停止から再開する。`stopped` からは再開しない。 */
  resume(): void;
  /** 計測を確定して止める。クリアした瞬間に呼ぶ。 */
  stop(): void;
  /** ゼロに戻して `idle`（未開始）にする。面の読み込みと「やりなおす」で呼ぶ。 */
  reset(): void;
  /** メインループから毎フレーム呼ぶ。`running` のときだけ加算する。 */
  update(dtMs: number): void;
}

export function createLevelTimer(): LevelTimer {
  let elapsed = 0;
  let phase: TimerPhase = 'idle';

  return {
    get elapsedMs(): number {
      return elapsed;
    },
    get phase(): TimerPhase {
      return phase;
    },

    start(): void {
      elapsed = 0;
      phase = 'running';
    },

    pause(): void {
      if (phase === 'running') phase = 'paused';
    },

    resume(): void {
      // stopped からは再開しない。確定した記録を後から伸ばせてはいけない。
      if (phase === 'paused' || phase === 'idle') phase = 'running';
    },

    stop(): void {
      phase = 'stopped';
    },

    reset(): void {
      elapsed = 0;
      phase = 'idle';
    },

    update(dtMs: number): void {
      if (phase !== 'running') return;
      // NaN や負の dt を加算して時間が壊れるのを防ぐ。
      if (!Number.isFinite(dtMs) || dtMs <= 0) return;
      elapsed += dtMs;
    },
  };
}

/** `93042` → `"1:33.04"`。HUD とランキングで共通に使う。 */
export function formatTime(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '--:--.--';
  const totalCs = Math.floor(ms / 10); // 1/100 秒
  const cs = totalCs % 100;
  const totalSec = Math.floor(totalCs / 100);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));
  return `${min}:${pad(sec)}.${pad(cs)}`;
}

/**
 * 制限時間つきの面で、残り時間を返す。制限が無ければ `null`。
 * 0 を下回ったら 0 に丸める（表示が負にならないように）。
 */
export function remainingMs(elapsedMs: number, timeLimitMs: number | null): number | null {
  if (timeLimitMs === null) return null;
  return Math.max(0, timeLimitMs - elapsedMs);
}

/** 残り時間が僅かで、HUD が警告表示に切り替わるべきか。METAGAME-SPEC §1.3。 */
export const TIME_WARNING_MS = 20_000;

export function isTimeCritical(elapsedMs: number, timeLimitMs: number | null): boolean {
  const left = remainingMs(elapsedMs, timeLimitMs);
  return left !== null && left <= TIME_WARNING_MS;
}
